/**
 * Simulated GET /api/field-status — the Live Clock-In / truck board.
 *
 * Shape is `FieldStatus` from src/lib/api/fieldStatus.ts.
 *
 * The PEOPLE are real: every row is a `time_clock_snapshots` row from the
 * production snapshot (employee, jobcode, job number, minutes on the clock),
 * enriched with that job's real address and customer from `v_jobs_full`.
 *
 * The TRUCKS are generated — GPS Insight is the only source of a vehicle
 * position and there is none in the snapshot — so crews are placed on the real
 * trucks that the simulated GPS board (./gps.ts) has out this morning. Because
 * that is a generated fact, this endpoint is labeled simulated.
 */
import { loadTable } from '~/demo/data';
import { seededInt } from './prng';
import { num, str } from './finance';
import { simulatedGpsDispatch } from './gps';

interface JobInfo { address: string | null; zip: string | null; customer: string | null }

async function jobIndex(): Promise<Map<string, JobInfo>> {
  const jobs = await loadTable('v_jobs_full');
  const map = new Map<string, JobInfo>();
  for (const j of jobs) {
    const key = str(j.job_number);
    if (!key || map.has(key)) continue;
    map.set(key, {
      address: [str(j.loss_address1), str(j.loss_city), str(j.loss_state)].filter(Boolean).join(', ') || null,
      zip: str(j.loss_zip) || null,
      customer: str(j.customer_name) || null,
    });
  }
  return map;
}

export async function simulatedFieldStatus() {
  const [snaps, jobs, gps] = await Promise.all([
    loadTable('time_clock_snapshots'), jobIndex(), simulatedGpsDispatch(),
  ]);

  const asOf = snaps.map((r) => str(r.captured_at)).filter(Boolean).sort().pop() ?? null;

  // Everyone's jobs for the day, from their own clock rows.
  const jobsToday = new Map<string, { job_number: string; minutes: number }[]>();
  for (const r of snaps) {
    const name = str(r.employee_name);
    const jobNumber = str(r.job_number);
    if (!name || !jobNumber) continue;
    const list = jobsToday.get(name) ?? [];
    const hit = list.find((v) => v.job_number === jobNumber);
    if (hit) hit.minutes = Math.max(hit.minutes, num(r.minutes_on_clock));
    else list.push({ job_number: jobNumber, minutes: num(r.minutes_on_clock) });
    jobsToday.set(name, list);
  }

  const entries = snaps.map((r) => {
    const jobNumber = str(r.job_number) || null;
    const info = jobNumber ? jobs.get(jobNumber) : undefined;
    const name = str(r.employee_name);
    return {
      qbtime_user_id: str(r.qbtime_user_id),
      employee_name: name,
      jobcode_name: str(r.jobcode_name),
      job_number: jobNumber,
      is_shop: r.is_shop === true,
      is_pto: r.is_pto === true,
      pto_kind: r.pto_kind == null ? null : str(r.pto_kind),
      started_at: str(r.started_at) || null,
      minutes_on_clock: r.minutes_on_clock == null ? null : num(r.minutes_on_clock),
      job_address: info?.address ?? null,
      job_zip: info?.zip ?? null,
      customer_name: info?.customer ?? null,
      jobs_today: jobsToday.get(name) ?? [],
    };
  });

  const shop = entries.filter((e) => e.is_shop || !e.job_number);
  const field = entries.filter((e) => !e.is_shop && e.job_number);

  const outTrucks = gps.today.trucks.filter((t) => t.status === 'out' || t.status === 'late');
  const truckRows = outTrucks.map((t) => ({
    vehicle_label: t.label,
    address: t.currentAddress || null,
    fix_time: t.gps.lastUpdate,
    lat: t.gps.lat,
    lon: t.gps.lon,
    age_minutes: t.gps.ageMinutes,
    people: [] as typeof field,
  }));

  // Spread the crews that are on a job across the trucks that rolled out,
  // deterministically so the same person keeps the same truck on a reload.
  if (truckRows.length) {
    for (const person of field) {
      const idx = seededInt(`${person.employee_name}|truck`, 0, truckRows.length - 1);
      truckRows[idx].people.push(person);
    }
  }
  const staffed = truckRows.filter((t) => t.people.length > 0);

  return {
    ok: true,
    simulated: true,
    demo_note:
      'Clock-in rows are real time_clock_snapshots enriched with v_jobs_full addresses; the trucks they sit on and '
      + 'the vehicle positions are generated (see the simulated GPS Insight board), because the snapshot holds no GPS.',
    asOf,
    counts: {
      clockedIn: entries.length,
      onTrucks: staffed.reduce((s, t) => s + t.people.length, 0),
      atShop: shop.length,
      trucksOut: outTrucks.length,
      trucksTotal: gps.today.trucks.length,
    },
    truckRows: staffed,
    shop,
  };
}
